import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Template from './template';
import { Label, MoveCleaningServiceRegister } from 'components';
import { TGuideType } from 'components/molecules/Popup/ImageGuide';
import { useAddress, useApiRequest } from 'hooks';
import { PartnerAuthState } from 'stores/PartnerAuthStore';
import { ServiceState, ServiceAction } from 'stores/ServiceStore';
import ZimssaConstant from 'commons/ZimssaConstant';
import {
  validateLength,
  validateInput,
  getYear,
  isValidServiceState,
} from 'commons/ZimssaHelper';
import { apiMoveCleaningTempSave, apiGetTempData } from 'api/ZimssaAPI';
import {
  PLACEHOLDER_SERVICE_NAME,
  BUTTON_NEXT,
  ALERT_NUMBER,
  PLACEHOLDER_SERVICE_REGIST_NUMBER,
  PAGE_SERVICE_MOVECLEANING_REGISTER_TITLE,
  SELECT_DEFAULT_YEAR,
} from 'commons/ZimssaString';
import { MOVECLEANING_PROFILE_REGISTER } from 'router/PageUrl';

function MoveCleaningServiceRegisterView(): React.ReactElement {
  const history = useHistory();
  const { partnerIdx } = useContext(PartnerAuthState);
  const {
    serviceName,
    serviceImage,
    introduce,
    startYear,
    businessRegistrationNumber,
    businessRegistrationImage,
    businessRegistrationOwner,
    taxClassification,
    familyRelationImage,
    location1,
    location2,
    serviceValid,
  } = useContext(ServiceState);
  const { province, city, getCityCallback } = useAddress(location1 || '');
  const serviceDispatcher = useContext(ServiceAction);
  const [fetchGetTempDataResult, fetchGetTempDataSet] =
    useApiRequest<any>(apiGetTempData);
  const [btnNextValid, setBtnNextValid] = useState<boolean>(false);

  //파트너 서비스, 활동 이미지(업체 로고)
  const serviceImgUploadDir =
    ZimssaConstant.IMG_DIR.SERVICE.MOVECLEANING.replace(
      '{{partnerIdx}}',
      `${partnerIdx}`
    );
  //파트너 서류 이미지(주선면허, 보험)
  const docImgUploadDir = ZimssaConstant.IMG_DIR.PARTNER.DOC.replace(
    '{{partnerIdx}}',
    `${partnerIdx}`
  );

  /**
   * 서비스 이름, 서비스 이미지는 가정이사, 이사청소에만 국한되는 valid 항목이기때문에,
   * 직접 view에서 해당 값이 있는지 체크해서 next 버튼 활성화하도록 작업
   */
  useEffect(() => {
    if (isValidServiceState(serviceValid, serviceName, serviceImage)) {
      setBtnNextValid(true);
    } else {
      setBtnNextValid(false);
    }
  }, [serviceName, serviceImage, serviceValid]);

  useEffect(() => {
    const { result, data } = fetchGetTempDataResult;

    if (result === 'success' && data) {
      serviceDispatcher({
        type: 'SET_STATE',
        value: {
          serviceImage: data.service.serviceImage,
          serviceName: data.service.serviceName,
          introduce: data.service.introduce,
          location1: data.service.location1,
          location2: data.service.location2,
          startYear: data.service.startYear,
          businessRegistrationNumber: data.service.businessRegistrationNumber,
          taxClassification: data.service.taxClassification,
          businessRegistrationImage: data.service.businessRegistrationImage,
          businessRegistrationOwner: data.service.businessRegistrationOwner,
          familyRelationImage: data.service.familyRelationImage,
        },
      });
    }
  }, [fetchGetTempDataResult, serviceDispatcher]);

  useEffect(() => {
    fetchGetTempDataSet({
      type: 'REQUEST',
      body: ['move_cleaning'],
    });
  }, [fetchGetTempDataSet]);

  const onChangeHandlerCallback = useCallback(
    (type: any, value: string) => {
      serviceDispatcher({
        type: type,
        value: value,
      });
    },
    [serviceDispatcher]
  );

  const onChangeCallback = useCallback(
    (e: any) => {
      const { name, value } = e.target;
      const type: any = name;

      serviceDispatcher({
        type: type,
        value: value,
      });
    },
    [serviceDispatcher]
  );

  const onChangeHandlerProvinceCallback = useCallback(
    (type: any, value: string) => {
      serviceDispatcher({
        type: type,
        value: value,
      });
      serviceDispatcher({
        type: 'location2',
        value: '',
      });
      getCityCallback(value);
    },
    [serviceDispatcher, getCityCallback]
  );

  const onClickNextCallback = useCallback(() => {
    if (serviceValid) {
      apiMoveCleaningTempSave({
        service: {
          serviceImage: serviceImage,
          serviceName: serviceName,
          introduce: introduce,
          startYear: startYear,
          businessRegistrationNumber: businessRegistrationNumber,
          businessRegistrationImage: businessRegistrationImage,
          businessRegistrationOwner: businessRegistrationOwner,
          taxClassification: taxClassification,
          familyRelationImage: familyRelationImage,
          location1: location1,
          location2: location2,
        },
      }).then((response) => {
        const { result, message } = response;
        if (result === 'success') {
          history.push(MOVECLEANING_PROFILE_REGISTER.url);
        } else {
          message && alert(message);
        }
      });
    }
  }, [
    history,
    serviceName,
    serviceImage,
    familyRelationImage,
    businessRegistrationImage,
    businessRegistrationNumber,
    businessRegistrationOwner,
    introduce,
    location1,
    location2,
    serviceValid,
    startYear,
    taxClassification,
  ]);

  const moveCleaningServiceRegisterProps = {
    serviceImage: {
      value: serviceImage,
      type: 'SERVICE' as TGuideType,
      inputName: 'serviceImage',
      imgDir: serviceImgUploadDir,
      onChangeHandler: useCallback(
        (type: any, value: string) => {
          serviceDispatcher({
            type: type,
            value: value,
          });
        },
        [serviceDispatcher]
      ),
    },
    serviceName: {
      inputProps: {
        inputName: 'serviceName',
        placeholder: PLACEHOLDER_SERVICE_NAME,
        value: serviceName,
        isValid: serviceName ? validateInput(serviceName.trim()) : true,
        maxLength: 30,
        onChange: onChangeCallback,
      },
    },
    introduce: {
      inputName: 'introduce',
      value: introduce,
      onChange: onChangeCallback,
    },
    location1: {
      inputName: 'location1',
      value: location1 ? location1 : '',
      children: province,
      onChangeHandler: onChangeHandlerProvinceCallback,
    },
    location2: {
      inputName: 'location2',
      value: location2 ? location2 : '',
      children: city,
      onChangeHandler: onChangeHandlerCallback,
    },
    startYear: {
      inputName: 'startYear',
      value: startYear ? startYear : 0,
      children: [{ name: SELECT_DEFAULT_YEAR, value: 0 }, ...getYear(100)],
      onChangeHandler: onChangeHandlerCallback,
    },
    businessRegistrationNumber: {
      inputProps: {
        inputName: 'businessRegistrationNumber',
        placeholder: PLACEHOLDER_SERVICE_REGIST_NUMBER,
        isValid: (() => {
          if (validateInput(businessRegistrationNumber.trim())) {
            if (validateLength(businessRegistrationNumber, 10)) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        })(),
        value: businessRegistrationNumber,
      },
      caption:
        validateInput(businessRegistrationNumber.trim()) &&
        !validateLength(businessRegistrationNumber, 10) ? (
          <Label
            contents={ALERT_NUMBER}
            color={'red_900'}
            fontStyle={'caption'}
          />
        ) : null,
      onChangeHandler: onChangeHandlerCallback,
    },
    taxClassification: {
      state: taxClassification,
      onClick: useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>, value?: any) => {
          serviceDispatcher({
            type: 'taxClassification',
            value,
          });
        },
        [serviceDispatcher]
      ),
    },
    //사업자 등록증사진
    businessRegistrationImage: {
      inputName: 'businessRegistrationImage',
      imgDir: docImgUploadDir,
      isDeleteBtn: true,
      value: businessRegistrationImage,
      onChangeHandler: onChangeHandlerCallback,
    },
    businessRegistrationOwner: {
      state: businessRegistrationOwner,
      onClick: useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>, value?: any) => {
          serviceDispatcher({
            type: 'businessRegistrationOwner',
            value: value,
          });

          if (value !== '가족') {
            serviceDispatcher({
              type: 'familyRelationImage',
              value: '',
            });
          }
        },
        [serviceDispatcher]
      ),
    },
    //가족관계 증명서
    familyRelationImage: {
      inputName: 'familyRelationImage',
      imgDir: docImgUploadDir,
      isDeleteBtn: true,
      value: familyRelationImage,
      guideMessageList: [
        '사업자 명의가 가족일 경우 가족관계증명서도 함께 업로드 부탁드립니다.',
      ],
      onChangeHandler: onChangeHandlerCallback,
    },

    btnNext: {
      contents: BUTTON_NEXT,
      isActive: btnNextValid,
      isComplete: false,
      onClick: onClickNextCallback,
    },
  };

  return (
    <Template title={PAGE_SERVICE_MOVECLEANING_REGISTER_TITLE}>
      <MoveCleaningServiceRegister {...moveCleaningServiceRegisterProps} />
    </Template>
  );
}

export default MoveCleaningServiceRegisterView;
